import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Signal, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { TranslocoModule } from '@ngneat/transloco';
import { ISvcChipOption, SvcControlsModule } from '@solvace/component/svc-controls';
import { SvcButtonsModule } from '@solvace/component/svc-buttons';
import { SvcAudioToTextButtonModule } from '@solvace/feature/general/svc-audio-to-text-button';
import { LibSharedCommonModule } from 'projects/lib-shared-common/src/public-api';

import { SvcActionPlanEscalateStore } from './svc-action-plan-escalate-store.service';

export interface SvcActionPlanEscalateModalData {
  usersOptions: ISvcChipOption[];
  systemsOptions: ISvcChipOption[];
  selectedResponsibleIds: Array<string | number>;
  selectedSystemIds: Array<string | number>;
  systemName: string;
  canChangeSystem?: boolean;
  isObeyaContext?: boolean;
  applicationId?: string;
  planSiteId?: number | null;
  planRoomId?: number | null;
  planBoardId?: number | null;
  systemId?: number | null;
  showVoiceToTextButton?: boolean;
  escalatedSiteId?: number | null;
}

export interface SvcActionPlanEscalateModalResult {
  responsibleIds: Array<string | number>;
  comment: string;
  systemId: number | null;
  obeya?: {
    siteId: number;
    roomId: number;
    boardId: number;
    systemUniqueId: number;
  };
}

interface SvcActionPlanEscalateForm {
  responsibleIds: FormControl<Array<string | number>>;
  comment: FormControl<string>;
  systemIds?: FormControl<Array<string | number>>;
  siteId?: FormControl<number[]>;
  roomId?: FormControl<number[]>;
  boardId?: FormControl<number[]>;
}

@Component({
  selector: 'svc-action-plan-escalate-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    TranslocoModule,
    SvcControlsModule,
    SvcButtonsModule,
    SvcAudioToTextButtonModule,
    LibSharedCommonModule,
  ],
  providers: [SvcActionPlanEscalateStore],
  templateUrl: './svc-action-plan-escalate-modal.component.html',
  styleUrls: ['./svc-action-plan-escalate-modal.component.scss'],
})
export class SvcActionPlanEscalateModalComponent {
  readonly #fb = inject(FormBuilder);
  readonly #dialogRef = inject<MatDialogRef<SvcActionPlanEscalateModalComponent, SvcActionPlanEscalateModalResult>>(MatDialogRef);
  readonly #destroyRef = inject(DestroyRef);
  readonly data = inject<SvcActionPlanEscalateModalData>(MAT_DIALOG_DATA);
  public readonly store = inject(SvcActionPlanEscalateStore);

  public readonly testIdPrefix = 'action-plan-escalate';
  public readonly isObeyaContext = !!this.data?.isObeyaContext;
  public readonly showVoiceToTextButton = this.data?.showVoiceToTextButton ?? true;
  public readonly usersOptions = this.data?.usersOptions ?? [];
  public readonly systemsOptions = this.data?.systemsOptions ?? [];
  public readonly canChangeSystem = !!this.data?.canChangeSystem;

  readonly #siteScopedUsers = this.isObeyaContext || (this.data?.escalatedSiteId ?? 0) > 0;
  public readonly responsibleOptions: Signal<ISvcChipOption[]> = computed(() =>
    this.#siteScopedUsers ? this.store.users() : this.usersOptions
  );

  public readonly form: FormGroup<SvcActionPlanEscalateForm>;
  public readonly isChangeDisabled: Signal<boolean>;
  public readonly systemName: Signal<string> = computed(() =>
    this.isObeyaContext ? this.store.lockedSystemName() : (this.data?.systemName ?? '')
  );

  readonly #requiredSelection: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    Array.isArray(control?.value) && control.value.length > 0 ? null : { required: true };

  constructor() {
    this.form = this.#buildForm();
    this.isChangeDisabled = this.#buildIsChangeDisabled();

    if (this.isObeyaContext) {
      this.#initObeyaSection();
    } else if (this.#siteScopedUsers) {
      this.store.initUsersForSite(this.data?.escalatedSiteId ?? null);
    }
  }

  public onClose(): void {
    this.#dialogRef.close();
  }

  public onChange(): void {
    if (this.isChangeDisabled()) {
      this.form.markAllAsTouched();
      return;
    }

    const responsibleIds = this.form.controls.responsibleIds.value ?? [];
    const comment = this.form.controls.comment.value ?? '';

    if (this.isObeyaContext) {
      const systemUniqueId = this.store.lockedSystem()?.systemUniqueId ?? 0;
      this.#dialogRef.close({
        responsibleIds,
        comment,
        systemId: this.store.lockedSystem()?.systemUniqueId ?? null,
        obeya: {
          siteId: this.#firstId(this.form.controls.siteId?.value) ?? 0,
          roomId: this.#firstId(this.form.controls.roomId?.value) ?? 0,
          boardId: this.#firstId(this.form.controls.boardId?.value) ?? 0,
          systemUniqueId,
        },
      });
      return;
    }

    const selectedSystemId = this.#firstId(this.form.controls.systemIds?.value);
    this.#dialogRef.close({
      responsibleIds,
      comment,
      systemId: selectedSystemId ?? this.data?.systemId ?? null,
    });
  }

  #buildForm(): FormGroup<SvcActionPlanEscalateForm> {
    const form = this.#fb.group<SvcActionPlanEscalateForm>({
      responsibleIds: this.#fb.control<Array<string | number>>(this.data?.selectedResponsibleIds ?? [], {
        nonNullable: true,
        validators: [this.#requiredSelection],
      }),
      comment: this.#fb.control<string>('', { nonNullable: true, validators: [Validators.maxLength(200)] }),
    });

    if (this.isObeyaContext) {
      form.addControl('siteId', this.#fb.control<number[]>([], { nonNullable: true, validators: [this.#requiredSelection] }));
      form.addControl('roomId', this.#fb.control<number[]>([], { nonNullable: true, validators: [this.#requiredSelection] }));
      form.addControl('boardId', this.#fb.control<number[]>([], { nonNullable: true, validators: [this.#requiredSelection] }));
    } else {
      form.addControl(
        'systemIds',
        this.#fb.control<Array<string | number>>(this.data?.selectedSystemIds ?? [], {
          nonNullable: true,
          validators: [this.#requiredSelection],
        })
      );
    }

    return form;
  }

  #buildIsChangeDisabled(): Signal<boolean> {
    const status = toSignal(this.form.statusChanges, { initialValue: this.form.status });
    return computed(() => {
      if (status() !== 'VALID') return true;
      if (!this.isObeyaContext) return false;
      return this.store.lockedSystem() == null || this.store.isLoadingSiteData();
    });
  }

  #initObeyaSection(): void {
    this.store.init(this.data);

    this.#setControlEnabled(this.form.controls.roomId, false);
    this.#setControlEnabled(this.form.controls.boardId, false);
    this.#prefillSiteWhenResolved();
    this.#prefillRoomAndBoardWhenResolved();

    this.#watchSiteControl();
    this.#watchRoomControl();
  }

  #prefillRoomAndBoardWhenResolved(): void {
    const planRoomId = Number(this.data?.planRoomId ?? 0);
    if (!Number.isFinite(planRoomId) || planRoomId <= 0) return;

    toObservable(this.store.selectedRoomId)
      .pipe(
        filter((roomId): roomId is number => roomId != null),
        take(1),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((roomId) => {
        this.form.controls.roomId?.setValue([roomId], { emitEvent: false });
        this.#setControlEnabled(this.form.controls.roomId, true);
        this.#setControlEnabled(this.form.controls.boardId, true);
        this.#refreshValidity();
      });

    toObservable(this.store.prefilledBoardId)
      .pipe(
        filter((boardId): boardId is number => boardId != null),
        take(1),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((boardId) => {
        this.form.controls.boardId?.setValue([boardId], { emitEvent: false });
        this.#refreshValidity();
      });
  }

  #prefillSiteWhenResolved(): void {
    const immediateSiteId = this.store.selectedSiteId();
    if (immediateSiteId != null) {
      this.#applySitePrefill(immediateSiteId);
      return;
    }

    toObservable(this.store.selectedSiteId)
      .pipe(
        filter((siteId): siteId is number => siteId != null),
        take(1),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((siteId) => this.#applySitePrefill(siteId));
  }

  #applySitePrefill(siteId: number): void {
    this.form.controls.siteId?.setValue([siteId], { emitEvent: false });
    this.#setControlEnabled(this.form.controls.roomId, true);
    this.#refreshValidity();
  }

  #watchSiteControl(): void {
    this.form.controls.siteId?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
      const siteId = this.#firstId(value);
      this.store.setSite(siteId);

      this.form.controls.responsibleIds.reset([]);
      this.form.controls.roomId?.reset([], { emitEvent: false });
      this.form.controls.boardId?.reset([], { emitEvent: false });
      this.#setControlEnabled(this.form.controls.roomId, siteId != null);
      this.#setControlEnabled(this.form.controls.boardId, false);
    });
  }

  #watchRoomControl(): void {
    this.form.controls.roomId?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
      const roomId = this.#firstId(value);
      this.store.setRoom(roomId);

      this.form.controls.boardId?.reset([], { emitEvent: false });
      this.#setControlEnabled(this.form.controls.boardId, roomId != null);
    });
  }

  #setControlEnabled(control: AbstractControl | undefined, enabled: boolean): void {
    if (!control) return;
    if (enabled) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  #refreshValidity(): void {
    this.form.updateValueAndValidity({ onlySelf: false });
  }

  #firstId(value: Array<string | number> | null | undefined): number | null {
    const first = Array.isArray(value) ? value[0] : value;
    const parsed = Number(first ?? Number.NaN);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
}
